import { z } from 'zod'
import type { Vec3 } from '../core/math'
import { FACTION_IDS, VFX_EFFECT_IDS, type FactionId, type VfxEffectId } from '../render/contracts'
import { ASSET_ID } from './assetSchema'

const DEG = Math.PI / 180
const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const unit = z.number().min(0).max(1)
const vec3Tuple = z.tuple([z.number(), z.number(), z.number()])
const posVec3 = z.tuple([positive, positive, positive])
/** [min, max] with min ≤ max. */
const minMax = (item: z.ZodNumber): z.ZodType<[number, number]> =>
  z.tuple([item, item]).refine(([a, b]) => a <= b, { message: 'expected [min, max] with min ≤ max' })
/** [value at skill 0, value at skill 1], lerped by skill; either order. */
const lerpPair = (item: z.ZodNumber): z.ZodTuple<[z.ZodNumber, z.ZodNumber]> => z.tuple([item, item])
const axisTriple = z.object({ pitch: positive, yaw: positive, roll: positive })
const codes = z.array(z.string().min(1)).min(1)
const buttonIndex = z.number().int().min(0).max(31)
const axisIndex = z.number().int().min(0).max(7)
const mouseButton = z.number().int().min(0).max(4)

/** A string union checked against a runtime list (the lists live in `render/contracts.ts`). */
function oneOf<T extends string>(values: readonly T[], what: string): z.ZodType<T> {
  return z.custom<T>((v) => typeof v === 'string' && (values as readonly string[]).includes(v), {
    message: `expected a ${what}: ${values.join(', ')}`,
  })
}

const factionId = oneOf<FactionId>(FACTION_IDS, 'FactionId')
const vfxId = oneOf<VfxEffectId>(VFX_EFFECT_IDS, 'VfxEffectId')

// ---------------------------------------------------------------------------------------------
// Weapons (weapons.json)
// ---------------------------------------------------------------------------------------------

export const WEAPON_IDS = ['pdc', 'pulseLaser', 'torpedo', 'pdcTurret', 'flakTurret'] as const
export type WeaponId = (typeof WEAPON_IDS)[number]
export const weaponIdSchema = z.enum(WEAPON_IDS)
/** The turret family (M6 P20): projectile weapons that carry a `turret` block and aim themselves. */
export const TURRET_WEAPON_IDS: readonly WeaponId[] = ['pdcTurret', 'flakTurret']
/** Turret mounts per ship the turret step keeps state for (corvette and large hauler carry 2; spec §4). */
export const MAX_TURRET_MOUNTS = 4

/**
 * Turret behaviour (M6 P20, spec §4 "Turrets"): an auto-aiming mount that slews at `traverseDeg` per second
 * inside a cone of half-angle `arcDeg` around its mount's facing, picks the nearest hostile ship or torpedo
 * (the class in `prefer` first) within the weapon's range, leads it and fires once its barrel is within
 * `fireConeDeg` of the lead point.
 */
const turretRawSchema = z.object({
  /** Slew rate, degrees per second. */
  traverseDeg: positive,
  /** Half-angle of the field of fire around the mount facing, degrees (180 = all round). */
  arcDeg: z.number().positive().max(180),
  /** Fires only while the barrel is within this angle of the lead point, degrees. */
  fireConeDeg: positive,
  /** Which target class the turret engages first when both are in arc. */
  prefer: z.enum(['torpedo', 'ship']),
  /** A round passing within this many metres of a non-friendly torpedo destroys it (0 = cannot intercept). */
  interceptRadius: nonNegative,
  /** Holds fire while a non-hostile ship is within its collider radius + this many metres of the line of fire. */
  clearance: nonNegative,
})

const weaponCommon = {
  id: weaponIdSchema,
  name: z.string().min(1),
  /** Shots per second (torpedo: 1 per 1.5 s). */
  rate: positive,
  /** Muzzle speed in m/s, added to the shooter's velocity. */
  speed: positive,
  damage: positive,
  /** Raw-damage multiplier against shields (spec §6 `ms`). */
  vsShield: nonNegative,
  /** Raw-damage multiplier against hull (spec §6 `mh`). */
  vsHull: nonNegative,
  /** Energy per shot. */
  energyCost: nonNegative,
  /** Effects: muzzle flash, impact on hull or rock, impact on a shield, optional trail. */
  vfx: z.object({ muzzle: vfxId, impact: vfxId, shieldImpact: vfxId, trail: vfxId.optional() }),
  /** Key into the audio bank (G11). */
  sfx: z.string().min(1),
}

export const projectileWeaponRawSchema = z.object({
  ...weaponCommon,
  kind: z.literal('projectile'),
  /** Metres; the projectile expires after range ÷ speed seconds. */
  range: positive,
  /** Random cone half-angle per shot, degrees. */
  spreadDeg: nonNegative,
  /** Every Nth shot draws a tracer (1 = every shot). */
  tracerEvery: z.number().int().min(1),
  /** Present on the turret family only (M6 P20). */
  turret: turretRawSchema.optional(),
})

export const torpedoWeaponRawSchema = z.object({
  ...weaponCommon,
  kind: z.literal('torpedo'),
  turnRateDeg: positive,
  lifeSeconds: positive,
  /** Rounds per mount at spawn. */
  rounds: z.number().int().min(1).max(255),
  /** Detonates within this many metres of its target. */
  proximity: positive,
  /** The target must stay within `range` m and `coneDeg` of the nose for `seconds` to lock. */
  lock: z.object({ range: positive, coneDeg: positive, seconds: positive }),
})

export const weaponRawSchema = z.discriminatedUnion('kind', [projectileWeaponRawSchema, torpedoWeaponRawSchema])

export const weaponsFileSchema = z
  .object({
    /** Shots aim at the target's lead point when it is within this angle of the muzzle direction. */
    gimbalDeg: nonNegative,
    /**
     * Aim assist (D-247): when the target's lead point is within `coneDeg` of the muzzle, the guns turn toward
     * it by up to `gimbalDeg` × weight × level, where weight is 1 inside `fullWithin` × cone and eases to 0 at
     * the cone edge. `levels` are the per-setting strength multipliers (Off / Low / Standard / High).
     */
    aimAssist: z
      .object({
        coneDeg: nonNegative,
        gimbalDeg: nonNegative,
        fullWithin: unit,
        levels: z.object({ off: nonNegative, low: nonNegative, standard: nonNegative, high: nonNegative }),
        /** The level AI pilots fly at (never above what the player can pick). */
        aiLevel: z.enum(['off', 'low', 'standard', 'high']),
      })
      .refine((a) => a.fullWithin < 1 || a.coneDeg === 0, 'fullWithin must be below 1'),
    /** Fixed pool capacities (D-014). */
    pools: z.object({ projectiles: z.number().int().min(1), torpedoes: z.number().int().min(1) }),
    /**
     * M10 P5a7b (plan 2026-10-17-m10-p5a, Q8): crewed turret batteries on L/XL hulls. While `playerBatteries` is
     * false (absent = false; off until the W6 flip, review F5) every turret acts as before: no target-first, no hold
     * fire, one round per draw whatever a mount's `barrels` says.
     */
    batteries: z.strictObject({ playerBatteries: z.boolean() }).optional(),
    weapons: z.array(weaponRawSchema).min(1),
  })
  .superRefine((file, ctx) => {
    for (const id of WEAPON_IDS) {
      const n = file.weapons.filter((w) => w.id === id).length
      if (n !== 1) ctx.addIssue({ code: 'custom', message: `weapon ${id} must appear exactly once (found ${n})` })
    }
    for (const w of file.weapons) {
      if ((w.id === 'torpedo') !== (w.kind === 'torpedo')) {
        ctx.addIssue({ code: 'custom', message: `weapon ${w.id}: only the torpedo is kind "torpedo"` })
      }
      const isTurret = TURRET_WEAPON_IDS.includes(w.id)
      if (isTurret !== (w.kind === 'projectile' && w.turret !== undefined)) {
        ctx.addIssue({ code: 'custom', message: `weapon ${w.id}: only the turret family carries a "turret" block, and it must` })
      }
    }
  })

export type WeaponRaw = z.infer<typeof weaponRawSchema>
export type WeaponsFileRaw = z.infer<typeof weaponsFileSchema>

export interface WeaponVfx {
  muzzle: VfxEffectId
  impact: VfxEffectId
  shieldImpact: VfxEffectId
  trail?: VfxEffectId
}

interface WeaponCommon {
  id: WeaponId
  name: string
  rate: number
  speed: number
  damage: number
  vsShield: number
  vsHull: number
  energyCost: number
  vfx: WeaponVfx
  sfx: string
}
/** Runtime turret behaviour (see turretRawSchema). Angles in radians, traverse in radians per second. */
export interface TurretSpec {
  traverse: number
  arc: number
  fireCone: number
  prefer: 'torpedo' | 'ship'
  interceptRadius: number
  clearance: number
}
/** Runtime projectile weapon. Angles in radians. */
export interface ProjectileWeaponDef extends WeaponCommon {
  kind: 'projectile'
  range: number
  spread: number
  tracerEvery: number
  /** The turret family only (M6 P20). */
  turret?: TurretSpec
}
/** Runtime torpedo weapon. Angles in radians. */
export interface TorpedoWeaponDef extends WeaponCommon {
  kind: 'torpedo'
  turnRate: number
  lifeSeconds: number
  rounds: number
  proximity: number
  lock: { range: number; cone: number; seconds: number }
}
export type WeaponDef = ProjectileWeaponDef | TorpedoWeaponDef

export type AimAssistLevel = 'off' | 'low' | 'standard' | 'high'
export const AIM_ASSIST_LEVELS: readonly AimAssistLevel[] = ['off', 'low', 'standard', 'high']

/** Runtime aim assist. Angles in radians. */
export interface AimAssistData {
  cone: number
  gimbal: number
  fullWithin: number
  levels: Record<AimAssistLevel, number>
  aiLevel: AimAssistLevel
}

/** Runtime weapons data. Mutable so the dev panel can tune it live. */
export interface WeaponsData {
  /** Radians. */
  gimbal: number
  aimAssist: AimAssistData
  pools: { projectiles: number; torpedoes: number }
  /** M10 P5a7b: crewed batteries (see `weaponsFileSchema.batteries`); absent = off. */
  batteries?: { playerBatteries: boolean }
  /** Indexed by `ResolvedMount.weapon`. */
  weapons: WeaponDef[]
}

export function toWeaponsData(raw: WeaponsFileRaw): WeaponsData {
  return {
    gimbal: raw.gimbalDeg * DEG,
    aimAssist: {
      cone: raw.aimAssist.coneDeg * DEG,
      gimbal: raw.aimAssist.gimbalDeg * DEG,
      fullWithin: raw.aimAssist.fullWithin,
      levels: { ...raw.aimAssist.levels },
      aiLevel: raw.aimAssist.aiLevel,
    },
    pools: { ...raw.pools },
    ...(raw.batteries === undefined ? {} : { batteries: { ...raw.batteries } }),
    weapons: raw.weapons.map((w): WeaponDef => {
      if (w.kind === 'projectile') {
        const { spreadDeg, turret, ...rest } = w
        const def: ProjectileWeaponDef = { ...rest, vfx: { ...w.vfx }, spread: spreadDeg * DEG }
        if (turret) {
          def.turret = {
            traverse: turret.traverseDeg * DEG,
            arc: turret.arcDeg * DEG,
            fireCone: turret.fireConeDeg * DEG,
            prefer: turret.prefer,
            interceptRadius: turret.interceptRadius,
            clearance: turret.clearance,
          }
        }
        return def
      }
      const { turnRateDeg, lock, ...rest } = w
      return {
        ...rest,
        vfx: { ...w.vfx },
        turnRate: turnRateDeg * DEG,
        lock: { range: lock.range, cone: lock.coneDeg * DEG, seconds: lock.seconds },
      }
    }),
  }
}

/** Inverse of toWeaponsData, for the dev panel's "Copy JSON". */
export function toWeaponsFileRaw(data: WeaponsData): WeaponsFileRaw {
  return {
    gimbalDeg: data.gimbal / DEG,
    aimAssist: {
      coneDeg: data.aimAssist.cone / DEG,
      gimbalDeg: data.aimAssist.gimbal / DEG,
      fullWithin: data.aimAssist.fullWithin,
      levels: { ...data.aimAssist.levels },
      aiLevel: data.aimAssist.aiLevel,
    },
    pools: { ...data.pools },
    ...(data.batteries === undefined ? {} : { batteries: { ...data.batteries } }),
    weapons: data.weapons.map((w): WeaponRaw => {
      if (w.kind === 'projectile') {
        const { spread, turret, ...rest } = w
        const raw: WeaponRaw = { ...rest, vfx: { ...w.vfx }, spreadDeg: spread / DEG }
        if (turret) {
          raw.turret = {
            traverseDeg: turret.traverse / DEG,
            arcDeg: turret.arc / DEG,
            fireConeDeg: turret.fireCone / DEG,
            prefer: turret.prefer,
            interceptRadius: turret.interceptRadius,
            clearance: turret.clearance,
          }
        }
        return raw
      }
      const { turnRate, lock, ...rest } = w
      return {
        ...rest,
        vfx: { ...w.vfx },
        turnRateDeg: turnRate / DEG,
        lock: { range: lock.range, coneDeg: lock.cone / DEG, seconds: lock.seconds },
      }
    }),
  }
}

// ---------------------------------------------------------------------------------------------
// Ships (ships.json)
// ---------------------------------------------------------------------------------------------

export const loadoutItemSchema = z
  .object({
    /** HP_ socket on the ship asset. */
    socket: z.string().regex(/^HP_[A-Z0-9]+(?:_[A-Z0-9]+)*$/),
    weapon: weaponIdSchema,
    /** Hardpoint module asset id (`hp.*`) mounted on that socket. */
    module: z.string().regex(/^hp\.[a-z0-9_]+$/),
    group: z.enum(['primary', 'secondary']),
    /** Primary weapon slot (key 1 / key 2); 0 for secondaries. */
    slot: z.union([z.literal(0), z.literal(1)]),
  })
  .refine((l) => (l.weapon === 'torpedo') === (l.group === 'secondary'), {
    message: 'torpedoes are the only secondary weapon',
  })

export type LoadoutItem = z.infer<typeof loadoutItemSchema>

/**
 * A turret mount (M6 P20): a turret-family weapon on an HP_ socket, covering its weapon's arc around `facing`
 * (a ship-space direction). Turret mounts sit outside the pilot's loadout: they aim and fire themselves.
 */
export const turretMountSchema = z.object({
  socket: z.string().regex(/^HP_[A-Z0-9]+(?:_[A-Z0-9]+)*$/),
  weapon: weaponIdSchema.refine((id) => TURRET_WEAPON_IDS.includes(id), { message: 'turret mounts take a turret-family weapon' }),
  facing: vec3Tuple.refine(([x, y, z]) => x * x + y * y + z * z > 1e-6, { message: 'facing must be a non-zero direction' }),
  /**
   * M10 P5a7b (spec §3.2 "batteries"): rounds one mount fires per draw (one cooldown step, one aim, each round paying
   * its energy). Absent = 1. Read only by a crewed battery (an L/XL hull with `weapons.json batteries.playerBatteries`).
   */
  barrels: z.number().int().min(1).max(6).optional(),
})

export type TurretMountRaw = z.infer<typeof turretMountSchema>

/** Boom sockets one mining hull may list (M10 P5a12a: the Gannet's four cutter heads). */
export const MAX_MINING_BOOMS = 4

/**
 * A mining hull's boom sockets (M10 P5a12a; plan 2026-10-17-m10-p5a §0.3): one mining beam per listed HP_ socket on
 * the def's asset. Absent = today's one beam from mining.json `beam.socket`. Unread until P5a12b (N beams).
 */
export const shipMiningSchema = z.strictObject({
  booms: z
    .array(z.string().regex(/^HP_[A-Z0-9]+(?:_[A-Z0-9]+)*$/))
    .min(1)
    .max(MAX_MINING_BOOMS)
    .refine((b) => new Set(b).size === b.length, { message: 'a boom socket may be listed only once' }),
})

export type ShipMiningRaw = z.infer<typeof shipMiningSchema>

/** Ship size bands (hull tiers spec §2.1), smallest first. Thresholds, dock modes and presets live in bands.json. */
export const SHIP_BAND_IDS = ['S', 'M', 'L', 'XL'] as const
export type ShipBand = (typeof SHIP_BAND_IDS)[number]
export const shipBandSchema = z.enum(SHIP_BAND_IDS)

/** The flight fields a `flightBand` preset fills (bands.json `flightPresets`, M10 P1d). */
export const flightPresetSchema = z.strictObject({
  maxRateDeg: axisTriple,
  maxAngAccelDeg: axisTriple,
  faGain: positive,
  rotGain: positive,
  boostSpeedMult: z.number().min(1),
  boostThrustMult: z.number().min(1),
})

export type FlightPreset = z.infer<typeof flightPresetSchema>

/**
 * The ship def shape before its refines. Zod 4 refuses `.partial()` on a refined object, so the `extends`
 * entry's partial (below) is built from this; the refined `shipDefRawSchema` then checks the merged entry.
 */
const shipDefBaseSchema = z
  .object({
    id: z.string().min(1),
    /** Base def id (M10 P1d): the entry is that def's merged fields, then its `flightBand` preset, then its own. Kept on the merged def. */
    extends: z.string().min(1).optional(),
    /** Size band override; without it the band comes from the asset's bounds length (`ShipDef.sizeBand`). */
    band: shipBandSchema.optional(),
    /** Flight preset from bands.json `flightPresets`, applied after the base and before the entry's own fields. */
    flightBand: shipBandSchema.optional(),
    name: z.string().min(1),
    /** Model entry in assets.json (sockets, collider, bounds). */
    assetId: z.string().regex(ASSET_ID),
    faction: factionId,
    mass: positive,
    inertiaScale: positive,
    thrustForward: positive,
    thrustReverse: positive,
    thrustLateral: positive,
    thrustVertical: positive,
    maxSpeed: positive,
    absMaxSpeed: positive,
    boostSpeedMult: z.number().min(1),
    boostThrustMult: z.number().min(1),
    boostEnergyPerSec: positive,
    maxRateDeg: axisTriple,
    maxAngAccelDeg: axisTriple,
    faGain: positive,
    rotGain: positive,
    energy: positive,
    energyRegen: nonNegative,
    hull: positive,
    shield: nonNegative,
    /** Shield points per second once regeneration has started. */
    shieldRegen: nonNegative,
    /** Seconds after the last hit before shields regenerate. */
    shieldRegenDelay: nonNegative,
    loadout: z.array(loadoutItemSchema).min(1),
    /** Auto-aiming turret mounts (M6 P20); crewed on AI ships, automatic when the player flies the ship. */
    turrets: z.array(turretMountSchema).max(MAX_TURRET_MOUNTS).optional(),
    /** Mining booms (M10 P5a12a): HP_ sockets that each fire a mining beam; absent = the one mining.json beam. */
    mining: shipMiningSchema.optional(),
    /** Tunable offset added to the asset's EYE_PILOT socket (ship space, metres). */
    eyeOffset: vec3Tuple.default([0, 0, 0]),
    chaseOffset: vec3Tuple,
    chaseRotStiffness: positive,
  })

export const shipDefRawSchema = shipDefBaseSchema
  .refine((s) => s.maxSpeed * s.boostSpeedMult <= s.absMaxSpeed, {
    message: 'maxSpeed × boostSpeedMult must not exceed absMaxSpeed',
  })
  .refine((s) => new Set(s.loadout.map((l) => l.socket)).size === s.loadout.length, {
    message: 'a socket may carry only one loadout item',
  })

/**
 * A `ships.json` entry that names a base def (M10 P1d): every field but `id` and `extends` is optional and
 * nothing is defaulted, so a left-out field inherits. The merged entry is checked by `shipDefRawSchema`.
 */
export const shipDefExtendsEntrySchema = shipDefBaseSchema
  .extend({ eyeOffset: vec3Tuple })
  .partial()
  .extend({ id: z.string().min(1), extends: z.string().min(1) })

export type ShipDefExtendsEntry = z.infer<typeof shipDefExtendsEntrySchema>

/** One raw `ships.json` entry: a partial `extends` entry, or a full def. */
export const shipsFileEntrySchema = z.union([shipDefExtendsEntrySchema, shipDefRawSchema])

export type ShipsFileEntry = z.infer<typeof shipsFileEntrySchema>

/** The raw file. `extends` entries are merged by `data/shipExtends.ts` before the defs are resolved. */
export const shipsFileSchema = z
  .object({ ships: z.array(shipsFileEntrySchema).min(1) })
  .refine((f) => new Set(f.ships.map((s) => s.id)).size === f.ships.length, { message: 'duplicate ship id' })

export type ShipDefRaw = z.infer<typeof shipDefRawSchema>

export interface AxisTriple {
  pitch: number
  yaw: number
  roll: number
}

/** One muzzle in ship space (metres): position and unit firing direction. */
export interface Muzzle {
  pos: Vec3
  dir: Vec3
}

/** A loadout item resolved against the asset manifest (see shipResolve.ts). */
export interface ResolvedMount {
  /** Index into `GameData.weapons.weapons`. */
  weapon: number
  group: 'primary' | 'secondary'
  slot: 0 | 1
  socket: string
  module: string
  /** The ship's HP socket pose composed with each of the module's HP_MUZZLE_n poses, in n order. */
  muzzles: Muzzle[]
}

/** A turret mount resolved against the asset manifest: ship-space pivot and unit facing (M6 P20). */
export interface ResolvedTurret {
  /** Index into `GameData.weapons.weapons` (a projectile weapon with a `turret` block). */
  weapon: number
  socket: string
  /** Ship-space pivot (the socket position), metres. */
  pos: Vec3
  /** Ship-space unit direction the arc is centred on. */
  facing: Vec3
  /** Rounds per draw on a crewed battery (M10 P5a7b; the raw `barrels`, default 1). */
  barrels: number
}

/**
 * Runtime ship definition. Angles are radians. Mutable so the dev panel can tune it live.
 * `eyePoint`, `colliderRadius`, `hullHalfExtents`, `hullCentre`, `mounts` and `torpedoRounds` are derived from the asset manifest.
 */
export interface ShipDef extends Omit<ShipDefRaw, 'maxRateDeg' | 'maxAngAccelDeg'> {
  maxRate: AxisTriple
  maxAngAccel: AxisTriple
  /** EYE_PILOT socket + eyeOffset, ship space. */
  eyePoint: [number, number, number]
  /** Fallback ball radius: the asset's ball radius, or bounds.radius × HULL_BALL_FACTOR for hull colliders. */
  colliderRadius: number
  /**
   * Ship-space half size [x, y, z] (m) of the asset's bounds box, about `hullCentre`: the hull box the docking
   * autopilot centres on the bay axis and fits through the mouth, the same box the asset bayFit check turns (D-TBD).
   */
  hullHalfExtents: [number, number, number]
  /** Ship-space centre [x, y, z] (m) of the asset's bounds box (D-TBD): the ship origin is not always the hull's middle. */
  hullCentre: [number, number, number]
  mounts: ResolvedMount[]
  /** Torpedo rounds at spawn (sum over torpedo mounts). */
  torpedoRounds: number
  /** Turret mounts (M6 P20), empty for ships without turrets. */
  turretMounts: ResolvedTurret[]
  /** Size band (M10 P1d): the raw `band` override, else from the asset's bounds length. Derived; never copied back. */
  sizeBand: ShipBand
}

/** The manifest-derived part of a ShipDef (see shipResolve.ts). */
export type ShipAssetData = Pick<ShipDef, 'eyePoint' | 'colliderRadius' | 'hullHalfExtents' | 'hullCentre' | 'mounts' | 'torpedoRounds' | 'turretMounts' | 'sizeBand'>

export function toShipDef(raw: ShipDefRaw, resolved: ShipAssetData): ShipDef {
  const { maxRateDeg, maxAngAccelDeg, ...rest } = raw
  return {
    ...rest,
    maxRate: { pitch: maxRateDeg.pitch * DEG, yaw: maxRateDeg.yaw * DEG, roll: maxRateDeg.roll * DEG },
    maxAngAccel: { pitch: maxAngAccelDeg.pitch * DEG, yaw: maxAngAccelDeg.yaw * DEG, roll: maxAngAccelDeg.roll * DEG },
    ...resolved,
  }
}

/** Inverse of toShipDef, for copying tuned values back into ships.json (derived fields are dropped). */
export function toShipDefRaw(def: ShipDef): ShipDefRaw {
  const { maxRate, maxAngAccel, ...rest } = def
  const raw: ShipDefRaw & Partial<ShipAssetData> = {
    ...rest,
    maxRateDeg: { pitch: maxRate.pitch / DEG, yaw: maxRate.yaw / DEG, roll: maxRate.roll / DEG },
    maxAngAccelDeg: { pitch: maxAngAccel.pitch / DEG, yaw: maxAngAccel.yaw / DEG, roll: maxAngAccel.roll / DEG },
  }
  delete raw.eyePoint
  delete raw.colliderRadius
  delete raw.hullHalfExtents
  delete raw.hullCentre
  delete raw.mounts
  delete raw.torpedoRounds
  delete raw.turretMounts
  delete raw.sizeBand
  return raw
}

// ---------------------------------------------------------------------------------------------
// AI profiles (ai-profiles.json)
// ---------------------------------------------------------------------------------------------

export const aiProfileSchema = z.object({ id: z.string().min(1), skill: unit, bravery: unit })

/**
 * A band's stand-off tuning (M10 P4b5d, Q11): a frigate fights from range and lets its turrets do the work. Every
 * other tuning value (aim, reaction, flee, steering, patrol) stays the global one.
 */
export const aiBandTuningSchema = z
  .strictObject({
    /** Patrol → Engage when an enemy is within this range (m). */
    engageRange: positive,
    /** Engage holds this distance band from the target (m). */
    holdMin: positive,
    holdMax: positive,
    /** False: a hit from the rear wakes the pilot into Engage instead of Evade, so it never jinks. */
    evade: z.boolean(),
    /** False: no nose-to-nose break (which would send it into Evade). */
    antiJoust: z.boolean(),
    /** The shortest avoidance ray, in the hull's collider radii (replaces `avoidMinDistance`). */
    avoidRadii: positive,
  })
  .refine((b) => b.holdMin <= b.holdMax, { message: 'holdMin must not exceed holdMax' })
  .refine((b) => b.holdMax <= b.engageRange, { message: 'holdMax must not exceed engageRange' })

export type AiBandTuning = z.infer<typeof aiBandTuningSchema>

export const aiProfilesFileSchema = z
  .object({
    /** In escalation order: a wave repeat steps each pilot one entry down this list. */
    profiles: z.array(aiProfileSchema).min(1),
    tuning: z.object({
      /** Patrol → Engage when an enemy is within this range (m). */
      engageRange: positive,
      /** Engage holds this distance band behind the target (m). */
      holdMin: positive,
      holdMax: positive,
      /** Nose to nose inside this range triggers Evade (m). */
      antiJoustRange: positive,
      /** Both noses within this angle of the line between the ships counts as nose to nose. */
      antiJoustConeDeg: positive,
      /** Evade duration [min, max] s. */
      evadeSeconds: minMax(positive),
      /** Surge command while evading (0..1), no boost: a jinking break, not a run (D-048). */
      evadeThrottle: unit,
      /** Surge command while fleeing (0..1), no boost, so the player can run a wounded pilot down (D-048). */
      fleeThrottle: unit,
      /** Seconds between new random jink directions while evading [min, max]. */
      jinkSeconds: minMax(positive),
      /** Aim error sigma in degrees: [skill 0, skill 1], lerped by skill. */
      aimErrorDeg: lerpPair(nonNegative),
      /** Reaction delay (age of the aim data) in seconds: [skill 0, skill 1]. */
      reactionDelay: lerpPair(nonNegative),
      /** Flee when hull / max < (1 − bravery) × fleeHullFactor. */
      fleeHullFactor: unit,
      /** Obstacle-avoidance ray length in seconds of current velocity. */
      avoidLookahead: positive,
      /** Shortest avoidance ray (m), used when nearly stopped. */
      avoidMinDistance: nonNegative,
      steering: z.object({
        /** Pitch/yaw command per radian of heading error (clamped to ±1). */
        turnGain: positive,
        /** Roll command per radian of bank error (bank-to-turn). */
        rollGain: nonNegative,
        /** Surge command per metre of range error while holding distance. */
        rangeGain: nonNegative,
        /** Pitch/yaw command added per unit of avoidance urgency. */
        avoidGain: nonNegative,
      }),
      patrol: z.object({
        /** Waypoints are seeded within this radius of the spawn point (m). */
        radius: positive,
        /** A waypoint counts as reached within this distance (m). */
        arriveDistance: positive,
        /** Surge command while patrolling (0..1). */
        throttle: unit,
      }),
      /**
       * Per-band overrides (M10 P4b5d, Q11). `L`: the stand-off tuning an AI-flown L reads when the traffic pilot
       * recorded its def band as L (`setPilotBand`); every other pilot reads the fields above. Absent: everyone does.
       */
      bands: z.strictObject({ L: aiBandTuningSchema }).optional(),
    }),
  })
  .refine((f) => new Set(f.profiles.map((p) => p.id)).size === f.profiles.length, { message: 'duplicate AI profile id' })
  .refine((f) => f.tuning.holdMin <= f.tuning.holdMax, { message: 'holdMin must not exceed holdMax' })

export type AiProfile = z.infer<typeof aiProfileSchema>
export type AiProfilesFileRaw = z.infer<typeof aiProfilesFileSchema>
type AiTuningRaw = AiProfilesFileRaw['tuning']

/** Runtime AI tuning. Angles in radians. */
export interface AiTuning extends Omit<AiTuningRaw, 'aimErrorDeg' | 'antiJoustConeDeg'> {
  /** Aim error sigma [skill 0, skill 1], radians. */
  aimError: [number, number]
  antiJoustCone: number
}

/** Runtime AI data. Mutable so the dev panel can tune it live. */
export interface AiData {
  profiles: AiProfile[]
  tuning: AiTuning
}

export function toAiData(raw: AiProfilesFileRaw): AiData {
  const { aimErrorDeg, antiJoustConeDeg, ...rest } = raw.tuning
  return {
    profiles: raw.profiles.map((p) => ({ ...p })),
    tuning: {
      ...rest,
      evadeSeconds: [rest.evadeSeconds[0], rest.evadeSeconds[1]],
      jinkSeconds: [rest.jinkSeconds[0], rest.jinkSeconds[1]],
      reactionDelay: [rest.reactionDelay[0], rest.reactionDelay[1]],
      steering: { ...rest.steering },
      patrol: { ...rest.patrol },
      ...(rest.bands === undefined ? {} : { bands: { L: { ...rest.bands.L } } }),
      aimError: [aimErrorDeg[0] * DEG, aimErrorDeg[1] * DEG],
      antiJoustCone: antiJoustConeDeg * DEG,
    },
  }
}

/** Inverse of toAiData, for the dev panel's "Copy JSON". */
export function toAiProfilesFileRaw(data: AiData): AiProfilesFileRaw {
  const { aimError, antiJoustCone, ...rest } = data.tuning
  return {
    profiles: data.profiles.map((p) => ({ ...p })),
    tuning: {
      ...rest,
      steering: { ...rest.steering },
      patrol: { ...rest.patrol },
      ...(rest.bands === undefined ? {} : { bands: { L: { ...rest.bands.L } } }),
      aimErrorDeg: [aimError[0] / DEG, aimError[1] / DEG],
      antiJoustConeDeg: antiJoustCone / DEG,
    },
  }
}

// ---------------------------------------------------------------------------------------------
// Waves (waves.json)
// ---------------------------------------------------------------------------------------------

export const wavesFileSchema = z
  .object({
    /** Seconds from the start to wave 1, and from each cleared wave to the next. */
    intervalSeconds: positive,
    waves: z
      .array(z.object({ ships: z.array(z.object({ ship: z.string().min(1), profile: z.string().min(1) })).min(1) }))
      .min(1),
    repeat: z.object({
      /** 1-based wave numbers repeated, in order, after the last wave. */
      waves: z.array(z.number().int().min(1)).min(1),
      /** Profile steps added to every pilot per repeat cycle. */
      escalate: z.number().int().min(0),
      /** Highest profile id escalation reaches. */
      cap: z.string().min(1),
    }),
    spawn: z.object({
      /** Distance from the player [min, max] (m). */
      distance: minMax(positive),
      /** Half-angle around the spawn heading (−Z), degrees. */
      coneDeg: z.number().min(0).max(180),
    }),
  })
  .refine((f) => f.repeat.waves.every((n) => n <= f.waves.length), { message: 'repeat.waves must name existing waves' })

export type WavesFileRaw = z.infer<typeof wavesFileSchema>

export interface WaveShip {
  ship: string
  profile: string
  /** Index into `GameData.ships`. */
  defIndex: number
  /** Index into `GameData.aiProfiles.profiles`. */
  profileIndex: number
}

/** Runtime wave data with ship and profile ids resolved to indices. Angles in radians. */
export interface WavesData {
  intervalSeconds: number
  waves: { ships: WaveShip[] }[]
  /** `waves` are 1-based wave numbers; `capIndex` indexes `GameData.aiProfiles.profiles`. */
  repeat: { waves: number[]; escalate: number; cap: string; capIndex: number }
  spawn: { distance: [number, number]; cone: number }
}

/** Resolves ship and profile ids. Throws naming the wave and the unknown id. */
export function toWavesData(raw: WavesFileRaw, ships: readonly { id: string }[], profiles: readonly AiProfile[]): WavesData {
  const profileIndex = (id: string, where: string): number => {
    const i = profiles.findIndex((p) => p.id === id)
    if (i < 0) throw new Error(`waves.json ${where}: unknown AI profile "${id}"`)
    return i
  }
  return {
    intervalSeconds: raw.intervalSeconds,
    waves: raw.waves.map((w, wi) => ({
      ships: w.ships.map((s) => {
        const defIndex = ships.findIndex((d) => d.id === s.ship)
        if (defIndex < 0) throw new Error(`waves.json wave ${wi + 1}: unknown ship "${s.ship}"`)
        return { ship: s.ship, profile: s.profile, defIndex, profileIndex: profileIndex(s.profile, `wave ${wi + 1}`) }
      }),
    })),
    repeat: { ...raw.repeat, waves: [...raw.repeat.waves], capIndex: profileIndex(raw.repeat.cap, 'repeat.cap') },
    spawn: { distance: [raw.spawn.distance[0], raw.spawn.distance[1]], cone: raw.spawn.coneDeg * DEG },
  }
}

// ---------------------------------------------------------------------------------------------
// Asteroid field (field.json): docs/art/sheets/asteroid-set.md, "Field composition (M1)"
// ---------------------------------------------------------------------------------------------

/** Rock archetypes in index order (0-5 = A-F), as in the asteroid-set sheet. */
export const FIELD_ARCHETYPES = ['A', 'B', 'C', 'D', 'E', 'F'] as const
export type FieldArchetype = (typeof FIELD_ARCHETYPES)[number]
const archetypeMix = z.tuple([nonNegative, nonNegative, nonNegative, nonNegative, nonNegative, nonNegative])
const presetCounts = z.object({ high: z.number().int().min(0), medium: z.number().int().min(0), low: z.number().int().min(0) })

export const fieldConfigSchema = z
  .object({
    /** Flattened ellipsoid holding every rock (world metres). */
    envelope: z.object({ center: vec3Tuple, semiAxes: posVec3 }),
    /** No rock surface inside this radius of the spawn point (m). */
    pocketRadius: positive,
    /** Share of rocks placed in the Gaussian clumps; the rest is uniform background. */
    clumpShare: unit,
    /** Clump vertical sigma = this × clump sigma. */
    verticalSigmaScale: positive,
    clumps: z
      .array(
        z.object({
          center: vec3Tuple,
          sigma: positive,
          /** Multiplies one archetype's share inside this clump (the derelict clump: E × 3). */
          bias: z.object({ archetype: z.enum(FIELD_ARCHETYPES), factor: positive }).optional(),
        }),
      )
      .min(1),
    /** Straight corridors kept clear of rock surfaces (m). */
    lanes: z.array(z.object({ name: z.string().min(1), from: vec3Tuple, to: vec3Tuple, clearRadius: positive })),
    /** Mean-radius bins. `mix` = archetype percentages A-F. Presets nest: low ⊂ medium ⊂ high. */
    sizeBins: z.array(z.object({ radius: minMax(positive), count: presetCounts, mix: archetypeMix })).min(1),
    /** Spin by size: a rock uses the first rule whose maxRadius exceeds its radius; larger rocks are static. */
    spin: z.array(z.object({ maxRadius: positive, degPerSec: minMax(nonNegative) })),
    /** Static ball collider radius = rock mean radius × this. */
    colliderScale: positive,
    /**
     * The Harrow derelict: station origin (m), roll off the system plane, its −Z face turned toward
     * `faceToward`, and a radius kept clear of rock surfaces.
     */
    derelict: z.object({ position: vec3Tuple, rollDeg: z.number(), faceToward: vec3Tuple, clearRadius: positive }),
  })
  .superRefine((f, ctx) => {
    f.sizeBins.forEach((b, i) => {
      const { high, medium, low } = b.count
      if (!(low <= medium && medium <= high)) {
        ctx.addIssue({ code: 'custom', message: `sizeBins[${i}]: counts must nest low ≤ medium ≤ high` })
      }
      const sum = b.mix.reduce((a, v) => a + v, 0)
      if (Math.abs(sum - 100) > 1e-6) ctx.addIssue({ code: 'custom', message: `sizeBins[${i}]: mix must sum to 100 (got ${sum})` })
    })
    for (let i = 1; i < f.spin.length; i++) {
      if (f.spin[i].maxRadius <= f.spin[i - 1].maxRadius) {
        ctx.addIssue({ code: 'custom', message: 'spin rules must increase in maxRadius' })
      }
    }
  })

export type FieldConfigRaw = z.infer<typeof fieldConfigSchema>

export interface FieldDerelict {
  position: [number, number, number]
  /** Radians. */
  roll: number
  faceToward: [number, number, number]
  clearRadius: number
}

/** Runtime field configuration: spin rates in rad/s, derelict roll in radians. */
export interface FieldConfig extends Omit<FieldConfigRaw, 'spin' | 'derelict'> {
  spin: { maxRadius: number; radPerSec: [number, number] }[]
  derelict: FieldDerelict
}

export function toFieldConfig(raw: FieldConfigRaw): FieldConfig {
  const { spin, derelict, ...rest } = raw
  return {
    ...rest,
    spin: spin.map((s) => ({ maxRadius: s.maxRadius, radPerSec: [s.degPerSec[0] * DEG, s.degPerSec[1] * DEG] })),
    derelict: {
      position: derelict.position,
      roll: derelict.rollDeg * DEG,
      faceToward: derelict.faceToward,
      clearRadius: derelict.clearRadius,
    },
  }
}

// ---------------------------------------------------------------------------------------------
// Bindings (bindings.json) and world (world.json)
// ---------------------------------------------------------------------------------------------

export const bindingsSchema = z.object({
  keyboard: z.object({
    surgeForward: codes,
    surgeBack: codes,
    swayLeft: codes,
    swayRight: codes,
    heaveUp: codes,
    heaveDown: codes,
    rollLeft: codes,
    rollRight: codes,
    boost: codes,
    toggleFA: codes,
    toggleCamera: codes,
    pause: codes,
    devPanel: codes,
    perfOverlay: codes,
    selectPrimary1: codes,
    selectPrimary2: codes,
    target: codes,
    /**
     * W2 D2 (dashboard spec §1.1, D-617): open and close the flight dashboard (Tab). The dashboard feature reads these
     * codes in its own capture-phase keydown listener (it relocks the pointer inside the gesture), so this is not a
     * polled keyboard edge; the pad's View tap is (`buttons.dashboard`).
     */
    dashboard: codes,
    /** M2 (plan §2.4): Combat → Mining → Salvage → Combat. W2 D2: Backquote (Tab is the dashboard). */
    toolCycle: codes,
    /** M2: Mining mode directly. */
    toolMining: codes,
    /** M2: Salvage mode directly. */
    toolSalvage: codes,
    /** M2: held = scan the nav target (Mining and Salvage modes). */
    scan: codes,
    /** M2: request docking clearance, or start auto-dock when cleared and close. */
    dock: codes,
    /** M4 (plan I6, spec §16): open the Reach Wire panel. */
    wirePanel: codes,
    /** M5 (plan C17, spec §19): open the Charter Board. */
    charterBoard: codes,
    /** M6 (plan P28, §9): open the Fleet screen. */
    fleetScreen: codes,
    /** M6 (plan P31): hold to open the wing radial; aim with the mouse, release to give the highlighted order. */
    wingMenu: codes,
    /** M6 (plan P35/P37): the fleet alert strip's "jump to" (the galaxy map on the alert's system). */
    fleetAlertJump: codes,
    /** M6 (plan P35/P37): the fleet alert strip's "send wing" (an asset under attack here). */
    fleetAlertWing: codes,
    /**
     * M10 P5a7b (plan Q8, review F20): toggle hold fire on your ship's crewed turret batteries (Digit3, beside the
     * primary groups 1 and 2; H is `fleetAlertJump` and every letter is taken). Inert while
     * `weapons.json batteries.playerBatteries` is off.
     */
    turretHoldFire: codes,
    /**
     * M7 (plan S31, §11 question 8): answer the oldest waiting hail (opens the comms panel). O, not the plan's H:
     * M6 bound H to `fleetAlertJump` (D-413/D-417). The pad answers from the dashboard's Log › Comms.
     */
    answerHail: codes,
    /** M7 (plan S31/S32, §11 question 8): open the Logs screen (S32 reads it; the pad uses the dashboard's Log › Case file). */
    logs: codes,
  }),
  mouse: z.object({
    sensitivity: positive,
    invertY: z.boolean(),
    deadzone: z.number().min(0).max(0.5),
    exponent: z.number().min(1),
    recenterRate: nonNegative,
    /** `MouseEvent.button` values while pointer-locked. */
    firePrimaryButton: mouseButton,
    fireSecondaryButton: mouseButton,
    /**
     * M2: the wheel steps the mining laser power while pointer-locked (wheel up = more power unless `invert`).
     * `stepPixels`: pixel-mode wheel delta per step (a mouse notch is about 100; trackpads send many small deltas).
     */
    powerWheel: z.object({ enabled: z.boolean(), invert: z.boolean(), stepPixels: positive }),
  }),
  gamepad: z.object({
    deadzone: z.number().min(0).max(0.5),
    exponent: z.number().min(1),
    /** Analog trigger value that counts as pressed. */
    triggerThreshold: z.number().gt(0).max(1),
    axes: z.object({ sway: axisIndex, surge: axisIndex, yaw: axisIndex, pitch: axisIndex }),
    invert: z.object({ sway: z.boolean(), surge: z.boolean(), yaw: z.boolean(), pitch: z.boolean() }),
    buttons: z.object({
      heaveUp: buttonIndex,
      heaveDown: buttonIndex,
      rollLeft: buttonIndex,
      rollRight: buttonIndex,
      boost: buttonIndex,
      toggleFA: buttonIndex,
      /** M2: a tap (released before `dockHoldSeconds`) toggles the camera; held that long it docks. */
      toggleCamera: buttonIndex,
      pause: buttonIndex,
      firePrimary: buttonIndex,
      fireSecondary: buttonIndex,
      cyclePrimary: buttonIndex,
      target: buttonIndex,
      /**
       * W2 D2: View; a tap (released within `viewHoldSeconds`) toggles the dashboard; a longer hold is M3's map
       * (`reach-controls.json` `gamepad.map`).
       */
      dashboard: buttonIndex,
      /**
       * M2 tool cycle; W2 D2: R3, a tap released within `toolCycleTapSeconds` while `toolCycleGuardButton` (L3) stayed
       * up for the whole press, so the L3 + R3 cruise chord never also cycles the tool.
       */
      toolCycle: buttonIndex,
      /** M2: laser power steps (D-pad ← / →). */
      powerDown: buttonIndex,
      powerUp: buttonIndex,
    }),
    /**
     * M2: held = scan in Mining and Salvage modes. It shares X with `buttons.cyclePrimary` (X cycles weapons in
     * Combat mode), so it lives outside `buttons`, whose indices stay unique.
     */
    scanButton: buttonIndex,
    /** M2: seconds the `toggleCamera` button (D-pad ↓) is held to dock; a shorter press toggles the camera on release. */
    dockHoldSeconds: positive,
    /** A View press released within this many seconds is a `dashboard` tap (W2 D2; M3 opens the map on a longer hold). */
    viewHoldSeconds: positive,
    /**
     * W2 D2: a press of this button at any time during a `toolCycle` (R3) press cancels the tap: L3, the other half of
     * the cruise chord. It shares L3 with `buttons.boost`, so it lives outside `buttons`.
     */
    toolCycleGuardButton: buttonIndex,
    /** W2 D2: an R3 press released within this many seconds is a `toolCycle` tap (below jettison's R3 hold). */
    toolCycleTapSeconds: positive,
    /**
     * M6 (plan P31): seconds the `toggleFA` button (D-pad ↑) is held to open the wing radial; a shorter press toggles
     * flight assist on release.
     */
    wingMenuHoldSeconds: positive,
  }),
})

export const worldSchema = z.object({
  seed: z.number().int().nonnegative(),
  dust: z.object({ count: z.number().int().positive(), box: positive }),
  camera: z.object({ fovDeg: positive, near: positive }),
  /** Player spawn point (world metres) and the delay before a respawn (s). */
  spawn: z.object({ position: vec3Tuple, respawnDelay: nonNegative }),
})

export type Bindings = z.infer<typeof bindingsSchema>
export type WorldConfig = z.infer<typeof worldSchema>
